/* NetBoxRenderer.js */

"use strict";
/* global Slick */
const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");
const ImportScreenComponent = require("../veils/ImportExport/ImportExportScreen/ImportScreenComponent");
const ImportExportSummaryComponent = require("../veils/ImportExport/ImportExportSummary/ImportExportSummaryComponent");
const FileName = require("@gluenetworks/steve_utilities").fileName;
require("@gluenetworks/jstree");
const SchedulesTab = require("./tabs/Schedule");

const DeviceSyncing = require("./tabs/deviceSyncing");
const ConnectionsTab = require("./tabs/connections");
const FilterTab = require("./tabs/Filter");
const LinkTab = require("./tabs/Link");
const SiteSyncing = require("./tabs/siteSyncing");
const MapDataTab = require("./tabs/MapData");

const {
    Control: {
        Action: {Button},
        Boolean: {InputCheckbox},
    },
} = uiCore;

const NETBOX_TABS = [
    {label: "Connection", value: "connections"},
    {label: "Filter", value: "filter"},
    {label: "Link", value: "link"},
    {label: "Map", value: "mapData"},
    {label: "Sync Devices", value: "deviceSyncing"},
    {label: "Sync Sites", value: "siteSyncing"},
    {label: "Schedule", value: "schedule"},
];

/**
 * Handles rendering and UI interactions for the NetBox view in the CMDB integration module.
 * Provides a tabbed interface for managing connections, filtering, linking, syncing, and scheduling.
 *
 * @class NetBoxViewRenderer
 *
 * @listens NetBoxViewRenderer#event:OrgChange
 * @listens NetBoxViewRenderer#event:ResetAllValues
 * @listens NetBoxViewRenderer#event:FailedCMDBConnect
 * @listens NetBoxViewRenderer#event:ConnectionSuccessful
 * @listens NetBoxViewRenderer#event:ConnectionFailed
 * @listens NetBoxViewRenderer#event:SavedWithGoodConnection
 */
class NetBoxViewRenderer {
    /**
     *
     * @param {NetBoxViewComponent} model - The component model that manages data and business logic.
     * @param {UiUtils} uiUtils - Utility methods for UI operations, including dialogs and notifications.
     */
    constructor(model, uiUtils) {
        this._model = model;
        this._uiUtils = uiUtils;
        this._rootElem = document.createElement("cmdb-netbox-gl");
        this._rootElem.component = this._model;
        this._fileNamer = FileName;

        this._valueChange = this._valueChange.bind(this);
        this._connectionsTab = undefined;

        this._activeTab = "connections";

        this._errContainer = document.createElement("span");
        this._errContainer.classList.add("error-container");

        this._corruptNoticeShown = false;

        this._onlineIcon = document.createElement("base-icon-gl");
        this._onlineIcon.dataset.icon = "access.ok_with_white_bg";
        this._onlineIcon.setAttribute("title", "Connected");
        this._onlineIcon.classList.add("online-icon");

        this._offlineIcon = document.createElement("base-icon-gl");
        this._offlineIcon.dataset.icon = "access.unreachable_with_white_bg";
        this._offlineIcon.setAttribute("title", "Disconnected");
        this._offlineIcon.classList.add("online-icon");

        this._defaultSchedule;

        this._importButton = new Button({
            label: "Import",
            darkTheme: true,
        });

        this._exportButton = new Button({
            label: "Export",
            darkTheme: true,
            hidden: true,
        });

        this._cancelButton = new Button({
            label: "Cancel",
            darkTheme: true,
        });
        this._saveButton = new Button({
            label: "Save",
            darkTheme: true,
        });

        this._enableNetBox = new InputCheckbox({
            label: "Enable NetBox CMDB",
            labelShown: true,
            value: false, // Disabled By Default
            darkTheme: true,
        });

        this._tableOptions = {
            editable: true,
            enableCellNavigation: true,
            enableAsyncPostRender: true,
            forceFitColumns: true,
            autoHeight: false,
            autoEdit: true,
            resizable: false,
            rowHeight: 40,
        };

        this._treeViewForClasses = {};

        /* User Data - Start */
        this._userDataStorage = this._model._storageFactory.getRemoteStorageInstance(
            "views",
            "servicenow-config"
        );
        /* User Data - End */

        this._onNbTenantChange = this._onNbTenantChange.bind(this);
        this._onServiceConnectionChange = this._onServiceConnectionChange.bind(this);

        this._createConnectionsTab();

        this._createDeviceAndCiSyncingTab();

        this._createSiteAndCiSyncingTab();

        this._schedulesTab = undefined;

        this._linkTab = undefined;

        this._filterTab = undefined;
    }

    /*******************************************/
    /* START GETTERS AND SETTERS                 */
    /*******************************************/

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END GETTERS AND SETTERS                 */
    /* START COMMON RENDER METHODS             */
    /*******************************************/

    render() {
        if (this._model) {
            return this._model
                .whenModelIsReady()
                .then(() => {
                    this._schedulesTab = new SchedulesTab(
                        this._model._nbDataSyncConfigEntity,
                        this._model._logger,
                        this._model._userSession
                    );
                    this._schedulesTab._createLinkingAndSyncingScheduleTab();

                    this._linkTab = new LinkTab(this._model._nbDataSyncConfigEntity);
                    this._linkTab.createLinkTab();

                    this._filterTab = new FilterTab(
                        this._model._nbDataSyncConfigEntity,
                        this._model._customFieldsWithInherited,
                        this._model._deviceTypeBaseMap,
                        this._model._osMap,
                        this._model._vendorMap,
                        this._model._sitePathMap,
                        this._model._skuList,
                        this._model._metaData,
                        this._model._exclusionFilterOptions
                    );
                    this._filterTab.createFilterTab();

                    this._mapDataTab = new MapDataTab(
                        this._model._nbDataSyncConfigEntity,
                        this._model._muf,
                        this._uiUtils,
                        this._model._metaData
                    );
                })
                .then(() =>
                    Promise.allSettled([
                        this._schedulesTab.whenReady(),
                        this._mapDataTab.whenReady(),
                    ])
                )
                .then(() => {
                    this._rootElem.innerHTML = "";
                    this._rootElem.appendChild(this._enableNetBox._renderer._rootElem);
                    this._schedulesTab._enableDisableSchedules();
                    this._enableNetBox.setValue(
                        this._model._nbDataSyncConfigEntity.getEnabled() || false
                    );

                    this._createTabs();

                    const _buttonContainer = document.createElement("div");
                    _buttonContainer.classList.add("btn-container");
                    const _leftBtnContainer = document.createElement("div");
                    _leftBtnContainer.classList.add("left-btn-container");
                    _leftBtnContainer.appendChild(this._importButton._renderer._rootElem);
                    _leftBtnContainer.appendChild(this._exportButton._renderer._rootElem);

                    const _rightBtnContainer = document.createElement("div");
                    _rightBtnContainer.classList.add("right-btn-container");
                    _rightBtnContainer.appendChild(this._cancelButton._renderer._rootElem);
                    _rightBtnContainer.appendChild(this._saveButton._renderer._rootElem);

                    _buttonContainer.appendChild(_leftBtnContainer);
                    _buttonContainer.appendChild(_rightBtnContainer);

                    this._rootElem.appendChild(this._errContainer);
                    this._rootElem.appendChild(_buttonContainer);

                    return Promise.resolve();
                })
                .then(() => {
                    this._onTabClickEvent(undefined, "connections");

                    if (this._model._cmdbConnectionFailed) {
                        this._failedCMDBConnect();
                    } else {
                        this._toggleConnectionStatus(true);
                    }

                    return this._deviceSyncing._resetDnCSData();
                })
                .then(() => {
                    return this._siteSyncing._resetSnCSData();
                })
                .then(() => {
                    if (this._model._hasConnections) {
                        this._nbConnectionsContent.classList.remove("hidden");
                        this._noConnectionEl.classList.remove("show");
                    } else {
                        this._nbConnectionsContent.classList.add("hidden");
                        this._noConnectionEl.classList.add("show");
                    }
                    this._serviceConnectionSelection.setOptions(
                        this._connectionsTab._getConnectionOptions(this)
                    );
                    this._serviceConnectionSelection.setValue(
                        this._connectionsTab._getConnectionId(this)
                    );
                    this._nbTenant.setDisabled(this._model._cmdbConnectionFailed);
                    this._nbTenant.setOptions(this._connectionsTab._getNbTenantOpts(this));
                    this._nbTenant.setValue(this._connectionsTab._getNbTenantValue(this));
                    this._showCorruptConfigNotice();
                })
                .catch((err) => console.error(err) || Promise.reject(err));
        }
    }

    _createTabs() {
        this._tabParent = document.createElement("div");
        this._tabParent.classList.add("tabs");

        const _tabContentArr = [];

        this._tabElements = {};

        NETBOX_TABS.forEach((item) => {
            const _tabAction = document.createElement("button");
            this._tabElements[item.value] = _tabAction;
            _tabAction.classList.add("tab-btn");
            _tabAction.innerHTML = item.label;

            if (item.value === "connections") {
                _tabAction.appendChild(this._onlineIcon);
                _tabAction.appendChild(this._offlineIcon);
            }

            _tabAction.setAttribute("data-type", item.value);

            _tabAction.addEventListener(
                "click",
                (this._onTabClickEvent = this._onTabClickEvent.bind(this))
            );

            this._tabParent.appendChild(_tabAction);

            const _tabContent = document.createElement("div");
            _tabContent.classList.add("tab-content");
            _tabContent.setAttribute("data-type", item.value);

            _tabContent.appendChild(this._getTabContent(item.value));

            _tabContentArr.push(_tabContent);
        });

        this._rootElem.appendChild(this._tabParent);

        _tabContentArr.forEach((item) => {
            this._rootElem.appendChild(item);
        });

        this._validateCMDBSyncIntegrations();

        this._disableEnableTabs(!this._enableNetBox.getValue());

        this._registerListeners();
    }

    /*******************************************/
    /* END COMMON RENDER METHODS               */
    /* START REST OF THE TABS                  */
    /*******************************************/

    /*******************************************/
    /* END REST OF THE TABS                    */
    /* START COMMON ENTITY METHODS             */
    /*******************************************/

    _setEntity() {
        const serviceConnectionId = this._connectionsTab._getConnectionId(this);
        this._model._serviceConnectionEntity =
            this._model._getConnectionEntity(serviceConnectionId);
        if (this._model._serviceConnectionEntity) {
            this._model._nbDataSyncConfigEntity.setServiceConnectionId(
                this._model._serviceConnectionEntity.getId()
            );
        }

        this._model._nbDataSyncConfigEntity.setDataProvider("netBox");
        this._model._nbDataSyncConfigEntity.setDataProviderProduct("cmdb");
    }

    /*******************************************/
    /* END COMMON ENTITY METHODS               */
    /* START VALIDATION METHODS                */
    /*******************************************/

    _addRemoveTabErrorClass(elem, isValid) {
        if (!elem) {
            return;
        }
        if (isValid) {
            elem.classList.remove("errored");
        } else {
            elem.classList.add("errored");
        }
    }

    _validateCMDBSyncIntegrations() {
        if (this._connectionsTab._validateConnectionTab(this)) {
            this._nbTestButton.setDisabled(false);
        } else {
            this._nbTestButton.setDisabled(true);
        }

        const isFilterTabValid = this._filterTab.validateFilterTab();
        this._addRemoveTabErrorClass(this._tabElements["filter"], isFilterTabValid);

        const isMappingTabValid = this._mapDataTab.validateMapData();
        this._addRemoveTabErrorClass(this._tabElements["mapData"], isMappingTabValid);

        const isDeviceCILinkingTabValid = this._validatedeviceCILinkingTab();
        this._addRemoveTabErrorClass(this._tabElements["link"], isDeviceCILinkingTabValid);

        const isDeviceCISyncingTabValid = this._validatedeviceCISyncingTab();
        this._addRemoveTabErrorClass(this._tabElements["deviceSyncing"], isDeviceCISyncingTabValid);
        this._showErrorMessage("deviceSyncing");

        const isSiteCISyncingTabValid = this._validateSiteCiSyncingTab();
        this._addRemoveTabErrorClass(this._tabElements["siteSyncing"], isSiteCISyncingTabValid);
        this._showErrorMessage("siteSyncing");

        // Map tab duplicate-key errors are intentionally excluded from isAllValid: Save must stay
        // enabled so clicking it triggers the blocking error veil in _saveButtonAction(), instead of
        // silently disabling Save.
        const isAllValid =
            this._schedulesTab._validateLinkingSyncingTab() &&
            isDeviceCILinkingTabValid &&
            isDeviceCISyncingTabValid &&
            isSiteCISyncingTabValid &&
            isFilterTabValid &&
            this._linkTab.validateLinkTab();

        if (isAllValid) {
            if (this._model._nbDataSyncConfigEntity.isDirty()) {
                this._enableDisableSaveBtn(false);
                this._enableDisableCancelBtn(false);
            } else {
                this._enableDisableSaveBtn(true);
                this._enableDisableCancelBtn(true);
            }
        } else {
            this._enableDisableSaveBtn(true);
            if (this._model._nbDataSyncConfigEntity.isDirty()) {
                this._enableDisableCancelBtn(false);
            } else {
                this._enableDisableCancelBtn(true);
            }
        }
    }

    _validatedeviceCILinkingTab() {
        return !this._model._isDnCLErrored;
    }

    _validatedeviceCISyncingTab() {
        return !this._model._isDnCSErrored;
    }

    _validateSiteCiSyncingTab() {
        return !this._model._isSnCSErrored;
    }

    /*******************************************/
    /* END VALIDATION METHODS                  */
    /* START COMMON HELPER METHODS             */
    /*******************************************/

    _getTabContent(tabType) {
        switch (tabType) {
            case "connections":
                return this._getConnectionsContent();
            case "filter":
                return this._filterTab.getFilterTabContent();
            case "link":
                return this._linkTab.getLinkTabContent();
            case "schedule":
                return this._schedulesTab._getLinkingSyncingContent();
            case "deviceSyncing":
                return this._getdeviceCISyncingContent();
            case "siteSyncing":
                return this._getSiteCISyncingContent();
            case "mapData":
                return this._mapDataTab.getMapDataContent();
        }
    }

    _disableEnableTabs(state) {
        const tabsContainer = this._rootElem.querySelector(".tabs"),
            tabsContentContainers = this._rootElem.querySelectorAll(".tab-content");
        if (state) {
            tabsContainer.classList.add("disabled");
            tabsContentContainers.forEach((tabsContentContainer) => {
                tabsContentContainer.classList.add("disabled");
            });
        } else {
            tabsContainer.classList.remove("disabled");
            tabsContentContainers.forEach((tabsContentContainer) => {
                tabsContentContainer.classList.remove("disabled");
            });
        }
    }

    _enableDisableSaveBtn(val) {
        this._saveButton.setDisabled(val);
    }

    _enableDisableCancelBtn(val) {
        this._cancelButton.setDisabled(val);
    }

    _showError(error) {
        if (_.get(error, "errorCode") !== "NoPendingUpdates") {
            this._uiUtils.showMessageDialog({
                message: "An error occurred: " + this._uiUtils.errorCodeMapping(error),
                confirmButtonName: "OK",
            });
        }
    }

    /**
     * Warns once that part of the saved configuration could not be loaded. Deliberately not
     * awaited by render(), so the loading mask is torn down before the dialog appears.
     */
    _showCorruptConfigNotice() {
        if (
            this._corruptNoticeShown ||
            !this._filterTab ||
            !this._filterTab.hasCorruptExclusionFilters()
        ) {
            return;
        }

        this._corruptNoticeShown = true;
        this._uiUtils.showMessageDialog({
            message:
                "This configuration appears to be corrupt. The recoverable portions have been loaded — please review carefully and correct any issues before saving.",
            confirmButtonName: "OK",
        });
    }

    _showErrorMessage(tab) {
        switch (tab) {
            case "deviceSyncing":
                if (this._activeTab === tab) {
                    this._errContainer.innerText = "";
                    if (!this._validatedeviceCISyncingTab()) {
                        this._errContainer.innerText =
                            "Error - Type mismatch. Synced properties must use the matching data types.";
                    }
                }
                break;
            case "siteSyncing":
                if (this._activeTab === tab) {
                    this._errContainer.innerText = "";
                    if (!this._validateSiteCiSyncingTab()) {
                        this._errContainer.innerText =
                            "Error - Type mismatch. Synced properties must use the matching data types.";
                    }
                }
                break;
            default:
                this._errContainer.innerText = "";
                break;
        }
    }

    shutdownRenderer() {
        const promises = [
            this._getElementShutdownIfExists(this._rootElem),
            this._getElementShutdownIfExists(this._importButton),
            this._getElementShutdownIfExists(this._exportButton),
            this._getElementShutdownIfExists(this._cancelButton),
            this._getElementShutdownIfExists(this._saveButton),
            this._getElementShutdownIfExists(this._enableNetBox),
            this._getElementShutdownIfExists(this._urlTextBox),
            this._getElementShutdownIfExists(this._authMethod),
            this._getElementShutdownIfExists(this._clientIdTextBox),
            this._getElementShutdownIfExists(this._clientSecretCmbBox),
            this._getElementShutdownIfExists(this._userTextBox),
            this._getElementShutdownIfExists(this._passCredCmbBox),
            this._getElementShutdownIfExists(this._testButton),
            this._getElementShutdownIfExists(this._schedulesTab),
            this._getElementShutdownIfExists(this._addNewRowButton),
            this._getElementShutdownIfExists(this._addNewRowDnCSButton),
            this._getElementShutdownIfExists(this._rootElem),
            this._getElementShutdownIfExists(this._addNewRowCnDMButton),
            this._getElementShutdownIfExists(this._addNewConnectionButton),
        ];

        return Promise.all(promises).then(() => {
            this._deviceSyncing._unregisterEventListeners();
            this._siteSyncing._unregisterEventListeners();

            [
                "_rootElem",
                "_fileNamer",
                "_valueChange",
                "_importButton",
                "_exportButton",
                "_cancelButton",
                "_saveButton",
                "_activeTab",
                "_tabElements",
                "_tabParent",
                /* Connection Tab : Start */
                "_enableNetBox",
                "_urlTextBox",
                "_authMethod",
                "_clientIdTextBox",
                "_clientSecretCmbBox",
                "_userTextBox",
                "_passCredCmbBox",
                "_testButton",
                "_offlineIcon",
                "_onlineIcon",
                "_addNewConnectionButton",
                /* Connection Tab : End */

                /* Linking And Syncing Schedule : Start */
                "_schedulesTab",
                /* Linking And Syncing Schedule : End */

                /* Device And Ci Linking : Start */
                "_deviceAndCiLinkingContainer",
                "_treeClassesCont",
                "_addNewRowButton",
                "_slickTableDnCLContainer",
                /* Device And Ci Linking : End */

                /* Device And Ci Syncing : Start */
                "_deviceAndCiSyncingContainer",
                "_treeClassesCont",
                "_addNewRowDnCSButton",
                "_slickTableDnCSContainer",
                "_slickTableDnCSDerivedContainer",
                /* Device And Ci Syncing : End */

                /* Ci And Device Mapping : Start */
                "_classAndDeviceMappingContainer",
                "_addNewRowCnDMButton",
                "_slickTableCnDMContainer",
                "_slickCnDMTable",
                "_maxSortOrderCnDM",
                "_columnsCnDM",
                "_slickCnDMDataVIew",
                /* Ci And Device Mapping : End */

                /* Slick Items */
                "_tableOptions",
                "_slickDnCLTable",
                "_maxSortOrderDnCL",
                "_columnsDnCL",
                "_slickDnCSTable",
                "_slickDnCSDerivedTable",
                "_maxSortOrderDnCS",
                "_maxSortOrderDnCSDerived",
                "_columnsDnCS",
                "_columnsDnCSDerived",
                /* Slick Items */

                /* Tree Items */
                "_treeViewForClasses",
                "_treeViewForClasses2",
            ].forEach((elem) => {
                this[elem] = undefined;
            });
            /* Tree Items */

            return Promise.resolve();
        });
    }

    _getElementShutdownIfExists(elem) {
        return !_.isEmpty(elem) && elem.shutdown ? elem.shutdown() : Promise.resolve();
    }

    _entityContainsMapping() {
        if (_.isEmpty(this._model._nbDataSyncConfigEntity.getNamespaceMatchingCriteria())) {
            this._rootElem.querySelectorAll(".tab-content").forEach((node) => {
                if (
                    ["deviceCILinking", "deviceCISyncing"].indexOf(
                        node.getAttribute("data-type") || " "
                    ) > -1
                ) {
                    node.querySelector(".no-mapping").classList.add("show");
                }
            });
        } else {
            this._rootElem.querySelectorAll(".tab-content").forEach((node) => {
                if (
                    ["deviceCILinking", "deviceCISyncing"].indexOf(
                        node.getAttribute("data-type") || " "
                    ) > -1
                ) {
                    node.querySelector(".no-mapping").classList.remove("show");
                }
            });
        }
    }

    /*******************************************/
    /* END COMMON HELPER METHODS               */
    /* START COMMON EVENT HANDLER METHODS      */
    /*******************************************/

    _registerListeners() {
        // Generic Listeners : Start
        document.addEventListener("click", (this._clickHandler = this._clickHandler.bind(this)));
        this._saveButton.on(
            "UserClick",
            (this._saveButtonAction = this._saveButtonAction.bind(this))
        );
        this._cancelButton.on(
            "UserClick",
            (this._cancelButtonAction = this._cancelButtonAction.bind(this))
        );

        this._importButton.on("UserClick", (this._importAction = this._importAction.bind(this)));
        this._exportButton.on("UserClick", (this._exportAction = this._exportAction.bind(this)));
        this._model.on("OrgChange", (this._orgChange = this._orgChange.bind(this)));
        this._model.on("ResetAllValues", (this._resetAllValues = this._resetAllValues.bind(this)));
        this._model.on(
            "FailedCMDBConnect",
            (this._failedCMDBConnect = this._failedCMDBConnect.bind(this))
        );
        this._model.on(
            "SavedWithGoodConnection",
            (this._savedWithGoodConnection = this._savedWithGoodConnection.bind(this))
        );
        this._model.on(
            "FailedCMDBConnect",
            (this._failedCMDBConnect = this._failedCMDBConnect.bind(this))
        );
        this._model.on(
            "ConnectionSuccessful",
            (this._connectionSuccessful = this._connectionSuccessful.bind(this))
        );
        this._model.on(
            "ConnectionFailed",
            (this._connectionFailed = this._connectionFailed.bind(this))
        );
        this._model.on("NoVendorMap", (this._noVendorMaps = this._noVendorMaps.bind(this)));
        this._model.on(
            "validateCMDBSyncIntegrations",
            (this._validateCMDBSyncIntegrations = this._validateCMDBSyncIntegrations.bind(this))
        );
        // Generic Listeners : End

        // Connection Tab Listeners : Start
        this._connectionsTab._registerConnectionTabEvents(this);
        // Connection Tab Listeners : End

        // Schedule Tab Listeners : Start
        this._schedulesTab._registerListeners();
        this._schedulesTab.on("PreviewWork", (this._previewWork = this._previewWork.bind(this)));
        this._schedulesTab.on("RunWork", (this._runWork = this._runWork.bind(this)));
        this._schedulesTab.on(
            "DownloadLastWork",
            (this._downloadLastWork = this._downloadLastWork.bind(this))
        );
        this._schedulesTab.on(
            "ValueChange",
            (this._scheduleChangeHandler = this._scheduleChangeHandler.bind(this))
        );
        this._mapDataTab.on(
            "ValueChange",
            (this._mapDataChangeHandler = this._mapDataChangeHandler.bind(this))
        );
        // Schedule Tab Listeners : End

        // Link Tab Listeners : Start
        this._linkTab.on(
            "ConfigChanged",
            (this._linkChangeHandler = this._linkChangeHandler.bind(this))
        );
        // Link Tab Listeners : End

        // Filter Tab Listeners : Start
        this._filterTab.on(
            "ConfigChanged",
            (this._linkChangeHandler = this._filterChangeHandler.bind(this))
        );
        // Filter Tab Listeners : End
    }

    _unregisterListeners() {
        // Generic Listeners : Start
        window.removeEventListener("resize", this._fitTableSize);
        document.removeEventListener("click", this._clickHandler);
        if (this._saveButton) {
            this._saveButton.removeListener("UserClick", this._saveButtonAction);
        }
        if (this._importButton) {
            this._importButton.removeListener("UserClick", this._importAction);
        }
        if (this._exportButton) {
            this._exportButton.removeListener("UserClick", this._exportAction);
        }
        this._model.removeListener("OrgChange", this._orgChange);
        this._model.removeListener("ResetAllValues", this._resetAllValues);
        this._model.removeListener("FailedCMDBConnect", this._failedCMDBConnect);
        this._model.removeListener("SuccessfulCMDBConnect", this._successfulCMDBConnect);
        this._model.removeListener("SavedWithGoodConnection", this._savedWithGoodConnection);
        this._model.removeListener("ConnectionSuccessful", this._connectionSuccessful);
        this._model.removeListener("ConnectionFailed", this._connectionFailed);
        this._model.removeListener("NoVendorMap", this._noVendorMaps);
        this._model.removeListener(
            "validateCMDBSyncIntegrations",
            this._validateCMDBSyncIntegrations
        );

        // Connection Tab Listeners : Start
        if (this._connectionsTab) {
            this._connectionsTab._unregisterConnectionTabEvents(this);
        }
        // Connection Tab Listeners : End

        // Schedule Tab Listeners : Start
        if (this._schedulesTab) {
            this._schedulesTab._unRegisterListeners();
            this._schedulesTab.removeListener("PreviewWork", this._previewWork);
            this._schedulesTab.removeListener("RunWork", this._runWork);
            this._schedulesTab.removeListener("DownloadLastWork", this._downloadLastWork);
            this._schedulesTab.removeListener("ValueChange", this._scheduleChangeHandler);
        }
        // Schedule Tab Listeners : End

        // Link Tab Listeners : Start
        if (this._linkTab) {
            this._linkTab.removeListener("ConfigChanged", this._linkChangeHandler);
        }
        if (this._filterTab) {
            this._filterTab.removeListener("ConfigChanged", this._filterChangeHandler);
        }
        // Link Tab Listeners : End
    }

    shutdown() {
        this._unregisterListeners();
        this._unregisterDnCLSlickTableListeners();
        this._unregisterDnCSSlickTableListeners();

        return Promise.resolve().then(() => {
            this._model = undefined;
            this._rootElem.remove();
            return this.shutdownRenderer();
        });
    }

    _updateTabsDataSyncEntity() {
        this._corruptNoticeShown = false;
        this._filterTab.setDataSyncEntity(this._model._nbDataSyncConfigEntity);
        this._schedulesTab.setDataSyncEntity(this._model._nbDataSyncConfigEntity);
        this._linkTab.setDataSyncEntity(this._model._nbDataSyncConfigEntity);
        this._mapDataTab.setDataSyncEntity(this._model._nbDataSyncConfigEntity);
    }

    async _resetAllValues() {
        this._connectionsTab._resetConnectionValue(this);
        this._nbTenant.setValue(this._connectionsTab._getNbTenantValue(this));
        this._filterTab._resetExclusionFilters();
        this._linkTab._resetLinkConfig();
        this._schedulesTab._resetValues();
        const currentTabEl = this._tabParent.querySelector(".selected");
        if (currentTabEl.getAttribute("data-type") === "mapData") {
            this._mapDataTab._resetValues();
        }
        // Resets both Device Syncing and Site Syncing tabs
        this._deviceSyncing._resetDnCSData();
    }

    _connectionSuccessful() {
        this._toggleConnectionStatus(true);
    }

    _connectionFailed() {
        this._toggleConnectionStatus(false);
    }

    _noVendorMaps() {
        this._rootElem.querySelectorAll(".tab-content").forEach((node) => {
            if (
                ["classNDeviceMapping", "deviceCILinking", "deviceCISyncing"].indexOf(
                    node.getAttribute("data-type") || " "
                ) > -1
            ) {
                node.querySelector(".no-vendor-map").classList.add("show");
            }
        });
    }

    _failedCMDBConnect() {
        this._toggleConnectionStatus(false);
        this._nbTenant.setOptions(this._connectionsTab._getNbTenantOpts(this));
        this._nbTenant.setValue("");
        this._nbTenant.setDisabled(true);

        this._filterTab._connectionFailed();
        this._linkTab._connectionFailed();
        this._deviceSyncing._connectionFailed();
        this._siteSyncing._connectionFailed();
        this._mapDataTab._connectionFailed();
    }

    _successfulCMDBConnect() {
        this._toggleConnectionStatus(true);

        this._filterTab._connectionPassed();
        this._linkTab._connectionPassed();
        this._nbTenant.setOptions(this._connectionsTab._getNbTenantOpts(this));
        this._nbTenant.setValue(this._connectionsTab._getNbTenantValue(this));
        this._nbTenant.setDisabled(false);

        this._deviceSyncing._connectionPassed();
        this._siteSyncing._connectionPassed();
        this._mapDataTab._connectionPassed();
    }

    async _savedWithGoodConnection() {
        this._successfulCMDBConnect();
        this._nbTenant.setDisabled(false);
        this._nbTenant.setOptions(this._connectionsTab._getNbTenantOpts(this));
        this._nbTenant.setValue(this._connectionsTab._getNbTenantValue(this));
    }

    _orgChange() {
        this._uiUtils
            .showMessageDialog({
                message: "You have changed organizations. The browser will be refreshed.",
                confirmButtonName: "OK",
            })
            .then(() => {
                this._model._router.reload();
            });
    }

    _enableChange(val) {
        if (val) {
            const msg = `Enabling this feature constitutes acceptance and understanding that Gluware will collect data from the configured NetBox. Gluware, Inc. is not liable or responsible for the accuracy, timeliness or usefulness of the data collected from this 3rd Party and presented in the Gluware application.`;
            return this._uiUtils
                .showMessageDialog({
                    message: msg,
                    confirmButtonName: "OK",
                })
                .then(() => {
                    this._model._nbDataSyncConfigEntity.setEnabled(true);
                    return this._model._saveEntity();
                })
                .then(() => {
                    this._disableEnableTabs(false);
                    return Promise.resolve();
                });
        } else {
            this._model._nbDataSyncConfigEntity.setEnabled(false);
            this._disableEnableTabs(true);
            return this._model._saveEntity();
        }
    }

    _onNbTenantChange(val) {
        const nbDataSyncMappings = this._model._nbDataSyncConfigEntity.getDataSyncMappings() || {};
        _.set(nbDataSyncMappings, "modelMapping.tenantId", val);
        this._model._nbDataSyncConfigEntity.setDataSyncMappings(nbDataSyncMappings);
        this._validateCMDBSyncIntegrations();
    }

    _onServiceConnectionChange(val) {
        this._model._nbDataSyncConfigEntity.setServiceConnectionId(val);
        this._model._serviceConnectionEntity = this._model._getConnectionEntity(val);
        this._validateCMDBSyncIntegrations();
    }

    async _onTabClickEvent(event, passedType = undefined) {
        // Clear Document Listeners
        this._deviceSyncing._unregisterDocumentListener();
        this._siteSyncing._unregisterDocumentListener();

        const currentType = passedType ? passedType : event.currentTarget.getAttribute("data-type");
        this._activeTab = currentType;
        this._showErrorMessage(currentType);

        const tabs = document.querySelectorAll(".tab-btn");
        tabs.forEach((tabVal) => {
            tabVal.classList.remove("selected");

            if (currentType === tabVal.getAttribute("data-type")) {
                tabVal.classList.add("selected");
            }
        });

        // Hide all tab contents
        const tabContents = document.querySelectorAll(".tab-content");
        tabContents.forEach((tabContent) => {
            tabContent.classList.remove("show");

            if (currentType === tabContent.getAttribute("data-type")) {
                tabContent.classList.add("show");
            }
        });

        if (currentType === "deviceCILinking") {
            this._entityContainsMapping(); // Call this when the tab is clicked and show no mapping message if needed
            const selectedNode =
                this._userDataStorage.getData("servicenow-linking-selected-node", true) || "1";
            this._treeViewForClasses.jstree("select_node", selectedNode);

            this._renderDnCLSlickGrid();
            this._renderDnCLDerivedSlickGrid();
            this._reapplyLinkingErrorRows();
        } else if (currentType === "deviceSyncing") {
            this._deviceSyncing._tabClick();
        } else if (currentType === "siteSyncing") {
            this._siteSyncing._tabClick();
        } else if (currentType === "mapData") {
            this._mapDataTab.render();
        }
    }

    async _exportAction() {
        try {
            await this._model._saveEntity();
            const exportResult = await this._model._nbDataSyncConfigEntity.runExportWork();

            this._model._muf.hideDisplayed();

            if (exportResult.summary.exportSuccessful) {
                const _fileName = this._fileNamer({
                    solution: "SS",
                    general: "CMDBIntegration",
                    specific: this._model._organizationData.name,
                });

                this._model._fileFactory.saveJsonAsFile(
                    _.get(exportResult, "summary.export"),
                    _fileName + ".json"
                );
            }

            const exportSummaryScreen = new ImportExportSummaryComponent(
                exportResult,
                this._model._logger,
                "export"
            );
            await this._model._muf.displayCustom(exportSummaryScreen, {
                dontHide: true,
            });

            this._model._muf.hideDisplayed();
        } catch (err) {
            this._model._muf.hideDisplayed();
            this.model._logger.warn("NetBoxViewRenderer._exportAction() error.");
        }
    }

    async _importAction() {
        try {
            let importScreenComponent = new ImportScreenComponent(
                this._model._logger,
                this._model._userSession,
                this._model._steveIo
            );
            const result = await this._model._muf.displayCustom(importScreenComponent, {
                dontHide: true,
            });

            const importFileProvider = _.get(result, "import.dataProvider");
            if (importFileProvider !== "netBox") {
                throw {
                    errorCode: "INVALID_IMPORT_FILE",
                    errorMsg:
                        "The uploaded import file is not a NetBox configuration file. Please check and try again.",
                    userRejected: false,
                };
            }
            const importResult = await this._model._nbDataSyncConfigEntity.runImportWork(
                result,
                {}
            );

            const importSummaryScreen = new ImportExportSummaryComponent(
                importResult,
                this._model._logger,
                "import"
            );

            await this._model._muf.displayCustom(importSummaryScreen, {
                dontHide: true,
            });

            // reload data
            this._model._nbDataSyncConfigEntity =
                await this._model._DataSyncConfigEntityDef.loadProviderConfig(
                    this._model.activeOrgId,
                    "netBox",
                    "cmdb"
                );
            this._updateTabsDataSyncEntity();
            this._resetAllValues();
            this._showCorruptConfigNotice();

            this._model._muf.hideDisplayed();
        } catch (err) {
            await this._model._muf.hideDisplayed();
            if (err.userRejected) {
                this.model._logger.warn("NetBoxViewRenderer._importAction() Cancelled.");
                return;
            }
            const errObj = {
                errorCode: _.get(err, "errorCode", ""),
                errorMsg: _.get(err, "errorMsg", ""),
                title: "Error",
            };
            try {
                await this._model._muf.displayError(errObj);
            } catch (err) {
                this.model._logger.warn("NetBoxViewRenderer._importAction() Error.");
                return;
            }

            this.model._logger.warn("NetBoxViewRenderer._importAction() Error.");
        }
    }

    async _saveButtonAction() {
        this._enableDisableSaveBtn(true);
        this._enableDisableCancelBtn(true);

        // Validate linking, device syncing, and site syncing before saving
        const linkingResult = this._model.validateLinking();
        if (!linkingResult.valid) {
            return this._model._muf.displayError({
                errorCode: "MAPPING_ERR",
                errorMsg: linkingResult.errorMsg,
                title: "Error",
            });
        }
        const deviceSyncResult = this._model.validateDeviceSyncing();
        if (!deviceSyncResult.valid) {
            return this._model._muf.displayError({
                errorCode: "MAPPING_ERR",
                errorMsg: deviceSyncResult.errorMsg,
                title: "Error",
            });
        }
        const siteSyncResult = this._model.validateSiteSyncing();
        if (!siteSyncResult.valid) {
            return this._model._muf.displayError({
                errorCode: "MAPPING_ERR",
                errorMsg: siteSyncResult.errorMsg,
                title: "Error",
            });
        }
        if (!this._mapDataTab.validateMapData()) {
            return this._model._muf
                .displayError({
                    errorCode: "MAPPING_ERR",
                    errorMsg:
                        "Either duplicate rows are present or required create/regex values are missing in the Map tab.",
                    title: "Error",
                })
                .finally(() => this._validateCMDBSyncIntegrations());
        }

        // Check if connection is empty
        const connectionId = this._connectionsTab._getConnectionId(this);

        if (!connectionId) {
            // Show confirmation veil/modal
            try {
                await this._model._muf.displayConfirmation(
                    "No connection is selected. Do you want to proceed? This will disable the integration.",
                    {
                        title: "No Connection Selected",
                        confirmDetails: {
                            text: "Save",
                            type: "additive",
                        },
                        rejectDetails: {
                            text: "Cancel",
                            type: "text",
                        },
                    }
                );
                // User confirmed: set enable to false
                this._model._nbDataSyncConfigEntity.setEnabled(false);
            } catch (err) {
                if (!err.userRejected) {
                    console.warn(err);
                } else {
                    // User cancelled: do not proceed
                    this._validateCMDBSyncIntegrations();
                    return;
                }
            }
        }

        return this._model
            ._saveEntity()
            .then(() => {
                if (!connectionId) {
                    this._disableEnableTabs(true);
                    this._enableNetBox.setValue(false);
                }

                this._validateCMDBSyncIntegrations();
            })
            .catch((err) => {
                this.model._logger.warn(err, "NetBoxViewRenderer._saveButtonAction() error.");
                return Promise.reject(err);
            });
    }

    _cancelButtonAction() {
        this._model.resetAllPatches();
    }

    _clickHandler(e) {
        // If we click delete column
        const target = e.target.closest("base-icon-gl");
        if (
            target &&
            target.getAttribute("role") === "button" &&
            target.dataset.action === "delete-nb-sync" &&
            target.dataset.uuid
        ) {
            const propName = target
                .closest(".ui-widget-content.slick-row")
                .querySelector(".slick-cell.l0.r0").innerText;
            this._deviceSyncing._deleteDnCSItem(target.dataset.uuid, "GW_TO_NB", propName);
        } else if (
            target &&
            target.getAttribute("role") === "button" &&
            target.dataset.action === "delete-nb-sync-nb-to-gw" &&
            target.dataset.uuid
        ) {
            const propName = target
                .closest(".ui-widget-content.slick-row")
                .querySelector(".slick-cell.l0.r0").innerText;
            this._deviceSyncing._deleteDnCSItem(target.dataset.uuid, "NB_TO_GW", propName);
        } else if (
            target &&
            target.getAttribute("role") === "button" &&
            target.dataset.action === "delete-nb-site-sync" &&
            target.dataset.uuid
        ) {
            const propName = target
                .closest(".ui-widget-content.slick-row")
                .querySelector(".slick-cell.l0.r0").innerText;
            this._siteSyncing._deleteSnCSItem(target.dataset.uuid, "GW_TO_NB", propName);
        } else if (
            target &&
            target.getAttribute("role") === "button" &&
            target.dataset.action === "delete-nb-site-sync-nb-to-gw" &&
            target.dataset.uuid
        ) {
            const propName = target
                .closest(".ui-widget-content.slick-row")
                .querySelector(".slick-cell.l0.r0").innerText;
            this._siteSyncing._deleteSnCSItem(target.dataset.uuid, "NB_TO_GW", propName);
        }

        this._saveAndCloseEditCellAction(e);
    }

    /*******************************************/
    /* END COMMON EVENT HANDLER METHODS        */
    /* START GENERIC  RENDERER METHODS         */
    /*******************************************/

    _createEmptyBanner(msg, className) {
        const msgContainer = document.createElement("div");
        msgContainer.classList.add(className);
        const innerSpan = document.createElement("span");
        innerSpan.innerText = msg;

        msgContainer.appendChild(innerSpan);

        return msgContainer;
    }

    /*******************************************/
    /* END GENERIC  RENDERER METHODS           */
    /* START GENERIC SLICK GRID METHODS        */
    /*******************************************/

    _saveAndCloseEditCellAction(e) {
        // Do nothing if no active editors
        if (this._slickDnCLTable && !this._slickDnCLTable.getEditorLock().isActive()) {
            return false;
        }

        if (this._slickDnCSTable && !this._slickDnCSTable.getEditorLock().isActive()) {
            return false;
        }

        // Get event bubbling path
        const path = e.path || e.composedPath();

        // Check if click was outside of current editing cell
        let outsideClick = true;
        _.each(path, (element) => {
            const elHasClass = (className) =>
                element.classList && element.classList.contains(className);

            if (elHasClass("slick-cell")) {
                const cellHasActiveEditor = elHasClass("active") && elHasClass("editable");
                outsideClick = !cellHasActiveEditor;
                return false;
            }

            if (element === this._slickTableDnCLContainer) {
                return false;
            }

            if (element === this._slickTableDnCSContainer) {
                return false;
            }

            if (element === this._slickTableDnCSDerivedContainer) {
                return false;
            }
        });

        if (outsideClick) {
            this._slickDnCLTable && this._slickDnCLTable.getEditorLock().commitCurrentEdit();
            this._slickDnCSTable && this._slickDnCSTable.getEditorLock().commitCurrentEdit();
        }
    }

    _registerRowMovePlugin(grid, dataView) {
        const moveRowsPlugin = new Slick.RowMoveManager({
            cancelEditOnDrag: true,
        });

        moveRowsPlugin.onBeforeMoveRows.subscribe(function (e, {insertBefore, rows}) {
            rows = [...rows].sort();
            if (_.first(rows) === insertBefore || _.last(rows) === insertBefore - 1) {
                e.stopPropagation();
                return false;
            }
            return true;
        });
        moveRowsPlugin.onMoveRows.subscribe((e, args) => {
            let extractedRows = [],
                left,
                right;
            let rows = args.rows;
            let insertBefore = args.insertBefore;
            let data = dataView.getItems();
            left = data.slice(0, insertBefore);
            right = data.slice(insertBefore, data.length);

            rows.sort(function (a, b) {
                return a - b;
            });

            for (let i = 0; i < rows.length; i++) {
                extractedRows.push(data[rows[i]]);
            }

            rows.reverse();

            for (let i = 0; i < rows.length; i++) {
                let row = rows[i];
                if (row < insertBefore) {
                    left.splice(row, 1);
                } else {
                    right.splice(row - insertBefore, 1);
                }
            }

            data = left.concat(extractedRows.concat(right));

            let selectedRows = [];
            for (let i = 0; i < rows.length; i++) {
                selectedRows.push(left.length + i);
            }
            dataView.setItems(data);

            grid.resetActiveCell();
            grid.setSelectedRows(selectedRows);
            grid.invalidateAllRows();
            grid.resizeCanvas();

            this._model._dataForSlickTable = dataView.getItems();

            this._setDnCLInEntityAndEnableDisableActions();
        });

        grid.registerPlugin(moveRowsPlugin);
    }

    _createNoDataPlaceholder() {
        const noDataPlaceholder = document.createElement("div");
        noDataPlaceholder.classList.add("no-data-placeholder");
        noDataPlaceholder.innerText = "No Data";
        return noDataPlaceholder;
    }

    _fitTableSize() {
        if (this._slickDnCLTable) {
            this._slickDnCLTable.resizeCanvas();
        }
    }

    /*******************************************/
    /* END GENERIC SLICK GRID METHODS          */
    /* START CONNECTION TAB                    */
    /*******************************************/

    /* INITIATION : START */

    _createConnectionsTab() {
        this._connectionsTab = new ConnectionsTab(this);
    }

    /* INITIATION : END */

    /* RENDER : START */

    _getConnectionsContent() {
        return this._connectionsTab._createConnectionsTabContent(this);
    }

    /* RENDER : END */

    /* EVENTS : Start */

    _valueChange() {
        this._setEntity();
        this._validateCMDBSyncIntegrations();
        this._enableDisableSchedules();
    }

    async _testSSConnectivityAction() {
        try {
            await this._model._runTestConnectionWork(this._model._serviceConnectionEntity);
        } catch (err) {
            if (err && err.userRejected) {
                return;
            }
            this.model._logger.warn(err, "NetBoxViewRenderer._testSSConnectivityAction() error.");
        }
    }

    _handleConnectionsLinkClick(evt) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this._newConnectionAction();
    }

    async _newConnectionAction() {
        const routeDetails = this._model._router.getRouteDetails();
        routeDetails.sr = "Organization/Connections";

        if (
            this._model._nbDataSyncConfigEntity.isSynchronized() &&
            this._model._nbDataSyncConfigEntity.isDirty()
        ) {
            try {
                await this._model._muf.displayConfirmation(
                    "You have unsaved changes on this page. If you navigate away, your changes will be lost. Do you want to proceed?",
                    {
                        title: "Unsaved Changes",
                        confirmDetails: {
                            text: "OK",
                            type: "destructive",
                        },
                        rejectDetails: {
                            text: "Cancel",
                            type: "text",
                        },
                    }
                );
                this._model._router.goTo(routeDetails);
            } catch (err) {
                if (!err.userRejected) {
                    console.warn(err);
                }
            }
        } else {
            this._model._router.goTo(routeDetails);
        }
    }

    /* EVENTS : End   */

    /* HELPERS : START */

    _toggleConnectionStatus(isConnected) {
        if (isConnected) {
            this._onlineIcon.classList.add("show");
            this._offlineIcon.classList.remove("show");
        } else {
            this._onlineIcon.classList.remove("show");
            this._offlineIcon.classList.add("show");
        }
    }

    /* HELPERS : END */

    /*******************************************/
    /* END CONNECTION TAB                      */
    /* START SYNCING AND LINKING SCHEDULE      */
    /*******************************************/

    /* INITIATION : START */

    /* INITIATION : END */

    /* RENDER : START */

    _getLinkingSyncingContent() {
        const _tabMainContent = document.createElement("div");
        _tabMainContent.classList.add("content-main");

        this._enableDisableSchedulesCheckbox.setValue(
            this._model._nbDataSyncConfigEntity.getScheduleEnabled() || false
        );

        if (!_.isEmpty(this._model._nbDataSyncConfigEntity.getSchedule())) {
            this._scheduleFrequencyComponent.setSchedule(
                this._model._nbDataSyncConfigEntity.getSchedule() || {}
            );
        } else {
            this._setDefaultSchedule();
        }

        _tabMainContent.appendChild(this._enableDisableSchedulesCheckbox._renderer._rootElem);
        _tabMainContent.appendChild(this._scheduleFrequencyComponent._renderer._rootElem);
        _tabMainContent.appendChild(this.scheduleButtonContainer);
        return _tabMainContent;
    }

    /* RENDER : END */

    /* EVENTS : START */

    _previewWork() {
        this._model.syncWork(true);
    }

    _runWork() {
        this._model.syncWork(false);
    }

    _downloadLastWork() {
        this._model.downloadLastSyncReport();
    }

    _scheduleChangeHandler() {
        this._validateCMDBSyncIntegrations();
    }

    _linkChangeHandler() {
        this._validateCMDBSyncIntegrations();
    }

    _filterChangeHandler() {
        this._validateCMDBSyncIntegrations();
    }

    _mapDataChangeHandler() {
        this._validateCMDBSyncIntegrations();
    }

    /* EVENTS : END */

    /*******************************************/
    /* END SYNCING AND LINKING SCHEDULE        */
    /* START DEVICE AND CI LINKING             */
    /*******************************************/

    /* RENDER - START */

    _renderDnCLSlickGrid(data) {
        if (!this._slickDnCLTable) {
            this._slickDnCLDataVIew = new Slick.Data.DataView();
            this._slickDnCLDataVIew.setItems(data || []);
            this._slickDnCLTable = new Slick.Grid(
                this._slickTableDnCLContainer,
                this._slickDnCLDataVIew,
                this._columnsDnCL,
                this._tableOptions
            );

            this._registerDnCLSlickTableListeners();
            this._slickDnCLTable.maxSortOrder = this._maxSortOrder;
            this._slickDnCLTable.setSelectionModel(new Slick.RowSelectionModel());
            this._registerRowMovePlugin(this._slickDnCLTable, this._slickDnCLDataVIew);

            this._slickTableDnCLContainer.appendChild(this._createNoDataPlaceholder());
        } else {
            this._resetDnCLData(this._model._selectedClass);
        }
    }

    _renderDnCLDerivedSlickGrid(data) {
        if (!this._slickDnCLDerivedTable) {
            this._slickDnCLDerivedDataVIew = new Slick.Data.DataView();
            this._slickDnCLDerivedDataVIew.setItems(data || []);
            this._slickDnCLDerivedTable = new Slick.Grid(
                this._slickTableDnCLDerivedContainer,
                this._slickDnCLDerivedDataVIew,
                this._columnsDnCLDerived,
                this._tableOptions
            );

            this._registerDnCLDerivedSlickTableListeners();
            this._slickDnCLDerivedTable.maxSortOrder = this._maxSortOrderDnCLDerived;
            this._slickDnCLDerivedTable.setSelectionModel(new Slick.RowSelectionModel());
            this._registerRowMovePlugin(
                this._slickDnCLDerivedTable,
                this._slickDnCLDerivedDataVIew
            );

            this._slickTableDnCLDerivedContainer.appendChild(this._createNoDataPlaceholder());
        }
    }

    /* RENDER - END */

    /* HELPERS _ START */

    async _resetDnCLData(selectedTreeNode = undefined) {
        try {
            await this._model._generateSlickDataForInstanceLinking(selectedTreeNode);
            return Promise.resolve();
        } catch (err) {
            return Promise.reject(err);
        }
    }

    _setDnCLInEntityAndEnableDisableActions() {
        this._model.setInstanceLinking();
        this._validateCMDBSyncIntegrations();
    }

    /* HELPERS - END */

    /* SLICK METHODS - START */

    /* Events - Start */

    _registerDnCLSlickTableListeners() {
        if (this._slickDnCLTable) {
            this._slickDnCLTable.onCellChange.subscribe(this._onDnCLCellChangeAction);
            this._slickDnCLDataVIew.onRowCountChanged.subscribe(this._onDnCLRowCountChange);
            this._slickDnCLDataVIew.onRowsChanged.subscribe(this._onDnCLRowsChanged);
        }
    }

    _registerDnCLDerivedSlickTableListeners() {
        if (this._slickDnCLDerivedTable) {
            this._slickDnCLDerivedTable.onCellChange.subscribe(this._onDnCLDerivedCellChangeAction);
        }
    }

    _unregisterDnCLSlickTableListeners() {
        if (this._slickDnCLTable) {
            this._slickDnCLTable.onCellChange.unsubscribe(this._onDnCLCellChangeAction);
            if (this._slickDnCLDataVIew) {
                this._slickDnCLDataVIew.onRowCountChanged.unsubscribe(this._onDnCLRowCountChange);
                this._slickDnCLDataVIew.onRowsChanged.unsubscribe(this._onDnCLRowsChanged);
            }
        }
    }

    _onDnCLRowCountChange(event, {/*previous,*/ current}) {
        if (!this._model._noVendorMap) {
            this._slickDnCLTable.invalidateAllRows();
            this._slickDnCLTable.resizeCanvas();
            this._toggleNoDataPlaceholder(!current, this._slickTableDnCLContainer);
        }
    }

    _onDnCLRowsChanged() {
        this._validateCMDBSyncIntegrations();
    }

    _onDnCLCellChangeAction() {
        this._setDnCLInEntityAndEnableDisableActions();
        this._reapplyLinkingErrorRows();
    }

    _onDnCLDerivedCellChangeAction(el, args) {
        this._model.addRemoveD2CLinkingIgnoreRule(args.item);
    }

    /* Events - End */

    /* Helpers - Start */

    _toggleNoDataPlaceholder(state, container) {
        container.classList.toggle("empty", state);
    }

    _errorInLinking(ids = []) {
        if (this._slickDnCLTable) {
            const slickData = this._slickDnCLTable.getData();
            if (slickData.getLength() > 0) {
                ids.forEach((id) => {
                    const rowIndex = slickData.getIdxById(id);
                    const currentRow = this._slickDnCLTable.getCanvasNode().children[rowIndex];
                    if (currentRow) {
                        currentRow.classList.add("error-row");
                    }
                });
            }
        }
    }

    _reapplyLinkingErrorRows() {
        if (this._slickDnCLTable) {
            const slickRows = this._slickDnCLTable.getCanvasNode().children;
            for (const currentRow of slickRows) {
                currentRow.classList.remove("error-row");
            }
        }
        if (!_.isEmpty(this._model.errLinkingIds)) {
            this._errorInLinking(this._model.errLinkingIds);
        }
    }

    /* Helpers - End */

    /* SLICK METHODS - END */

    /*******************************************/
    /* END DEVICE AND CI LINKING               */
    /* START DEVICE AND CI SYNCING             */
    /*******************************************/

    /* INITIATION : START */

    _createDeviceAndCiSyncingTab() {
        this._deviceSyncing = new DeviceSyncing(this._model);
    }

    _createSiteAndCiSyncingTab() {
        this._siteSyncing = new SiteSyncing(this._model);
    }
    /* INITIATION : END */

    /* RENDERING - START */
    _getdeviceCISyncingContent() {
        return this._deviceSyncing._getdeviceCISyncingContent();
    }

    _getSiteCISyncingContent() {
        return this._siteSyncing._getSiteCISyncingContent();
    }

    /* RENDERING - END */

    /* SLICK METHODS - START */

    /* Events - Start */

    _unregisterDnCSSlickTableListeners() {
        if (this._slickDnCSTable) {
            this._slickDnCSTable.onCellChange.unsubscribe(this._onDnCSCellChangeAction);
            if (this._slickDnCSDataVIew) {
                this._slickDnCSDataVIew.onRowCountChanged.unsubscribe(this._onDnCSRowCountChange);
                this._slickDnCSDataVIew.onRowsChanged.unsubscribe(this._onDnCSRowsChanged);
            }
        }
    }

    _onDnCSRowCountChange(event, {/*previous,*/ current}) {
        if (!this._model._noVendorMap) {
            this._slickDnCSTable.invalidateAllRows();
            this._slickDnCSTable.resizeCanvas();
            this._toggleNoDataPlaceholder(!current, this._slickTableDnCSContainer); // Create new no data holder
        }
    }

    _onDnCSRowsChanged() {
        this._validateCMDBSyncIntegrations();
    }

    _onDnCSCellChangeAction() {
        const slickRows = this._slickDnCSTable.getCanvasNode().children;
        for (let currentRow of slickRows) {
            currentRow.classList.remove("error-row");
        }

        this._setDnCSInEntityAndEnableDisableActions();
    }

    /* Events - End */

    /* Helpers - Start */

    _setDnCSInEntityAndEnableDisableActions() {
        this._model.setDataSyncMappings();
        this._validateCMDBSyncIntegrations();
    }

    /* Helpers - End */

    /* SLICK METHODS - END */

    /*******************************************/
    /* END DEVICE AND CI SYNCING               */
    /*******************************************/
}

module.exports = NetBoxViewRenderer;
